// Admin CRUD for item_kinds — what a cataloged thing IS, one short key at a
// time.
//
// WHY A TABLE AND NOT A CONSTANT. The board list was `BOARD_CODES` in
// skuGrammar.ts, so adding a board type meant a deploy, and every non-board stem
// (GMT, SCR, FRM) was invented at the keyboard by whoever typed the SKU. Both
// halves are now one admin-editable taxonomy, the same shape as vendor types,
// and the page is the same component: see TaxonomyAdmin.
//
// THE ONE THING THIS PAGE ADDS is the example chip. A kind's code_mode decides
// which of the two SKU grammars it belongs to, and "structured versus short" is
// a paragraph nobody reads. One live example per row is the whole explanation.

import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import TaxonomyAdmin, { TaxonomyConfig, TaxonomyMeta } from './TaxonomyAdmin';
import ItemKindForm, { CodeModeSegmented, keyFromLabel } from './ItemKindForm';
import useItemKindsStore, { ItemKind, normalizeKind } from '../stores/useItemKindsStore';
import { exampleCode } from '../Parts/skuGrammar';
import { Checkbox } from '../components/ui/Checkbox';

// Module scope per the DataTable contract: the array never changes identity, and
// what a cell cannot derive from its own row rides on `meta`, read at render
// time. Never memoize one of these cells.
const KIND_COLUMNS: ColumnDef<ItemKind, any>[] = [
  {
    id: 'code_mode',
    header: 'Code style',
    cell: ({ row, table }) => {
      const meta = table.options.meta as unknown as TaxonomyMeta<ItemKind>;
      const k = row.original;
      return (
        <CodeModeSegmented
          size="sm"
          ariaLabel={`Code style for ${k.label}`}
          value={k.code_mode}
          onChange={(next) =>
            meta.onPatch(k, { code_mode: next }, next === 'structured' ? 'Now a board code' : 'Now a short code')
          }
        />
      );
    },
  },
  {
    id: 'example',
    header: 'Example',
    meta: { cellClass: 'whitespace-nowrap' },
    // Quiet on purpose: same mono gray as the key beside it, no color of its
    // own. It is a caption for the control to its left, not a status.
    //
    // The fill is recessed on purpose. A DataTable row goes to surface-container-highest under the pointer and when selected, so a chip that RISES vanishes on exactly the row being read. bg-surface is the darkest step and no row state ever reaches it.
    cell: ({ row }) => (
      <span className="rounded-full bg-surface px-2 py-0.5 font-mono text-xs text-on-surface-variant">
        {exampleCode(row.original.key, row.original.code_mode)}
      </span>
    ),
  },
  {
    id: 'tracked',
    header: 'Tracked per unit',
    meta: { align: 'center' },
    cell: ({ row, table }) => {
      const meta = table.options.meta as unknown as TaxonomyMeta<ItemKind>;
      const k = row.original;
      return (
        <Checkbox
          size="sm"
          aria-label={`Tracked per unit for ${k.label}`}
          checked={k.tracked_per_instance_default}
          onChange={(e) =>
            meta.onPatch(
              k,
              { tracked_per_instance_default: e.target.checked },
              e.target.checked ? 'Tracked per unit' : 'Tracked by quantity',
            )
          }
        />
      );
    },
  },
];

const CONFIG: TaxonomyConfig<ItemKind> = {
  title: 'Item Kinds',
  endpoint: '/item-kinds',
  noun: 'item kind',
  nounPlural: 'item kinds',
  countField: 'item_count',
  countHeader: 'Items',
  newButtonLabel: '+ New Kind',
  submitLabel: 'Add kind',
  deleteBlockedTitle: (n) =>
    `${n} item${n === 1 ? '' : 's'} use${n === 1 ? 's' : ''} this kind. Retire it instead.`,
  width: 'max-w-5xl',
  keyColumn: {
    header: 'Key',
    transform: keyFromLabel,
    placeholder: 'PYR',
    // Only `header` renders here: NewRowForm (ItemKindForm) replaces the built-in
    // modal, which is the only reader of transform/placeholder/hint. Kept in step
    // with the form's own sentences so the two never disagree if that changes.
    hint: 'Two to four characters for a short code, two or three letters for a board code. It never changes after this.',
  },
  extraColumns: KIND_COLUMNS,
  // Its own form, because a kind needs a code style and a tracking default, and
  // because the SKU builder mounts the same component to add one in place.
  NewRowForm: ItemKindForm,
  // The page's list IS the list every picker reads, so seed the cache from it.
  // Otherwise a rename here would leave a stale label in the SKU builder for the
  // rest of the session.
  onLoaded: (rows) => useItemKindsStore.getState().setAll(rows.map(normalizeKind)),
  help: (
    <>
      <p>
        A kind says what a thing is: an autopilot, a grommet, a frame. It is the short code
        in the middle of a SKU, and it is how the catalog groups itself.
      </p>
      <p>
        <b>The key is the code itself.</b> On a board it is the segment in the SKU
        (FF-<b>PYR</b>-02-1-CH). On everything else it is the stem a short code is built
        from (<b>GMT</b>-7K3Q). Two to four characters, starting with a letter. It never
        changes, because items are attached to it.
      </p>
      <p>
        <b>The label is what people see</b>, and it is free to rename. Every item on the
        kind picks up the new name at once, and nothing else moves.
      </p>
      <p>
        <b>Code style decides where the kind turns up.</b> Board codes are offered in the
        SKU builder, where the rest of the board grammar follows. Short codes will name
        everything that is not a board.
      </p>
      <p>
        <b>Tracked per unit</b> is the starting point for new items of this kind: whether
        each physical unit gets its own record and sticker, or whether a count is enough.
        It stays changeable on the item.
      </p>
      <p>
        <b>Retire rather than delete.</b> Retiring stops a kind being offered for new items
        while leaving the items already on it alone. Delete only works while nothing carries
        the kind at all, which is what the Items count on each row tells you.
      </p>
      <p>
        <b>A BMS is its own kind.</b> It is a board, so it gets a board code. The battery is
        the cells, and that is a different thing you buy from a different vendor.
      </p>
    </>
  ),
};

const ItemKindsAdmin: React.FC = () => <TaxonomyAdmin config={CONFIG} />;

export default ItemKindsAdmin;
